// frontend/src/features/portfolio-diversification-optimizer-pro/components/OptimizationSimulatorPanel.tsx
//
// Pillar 5 -- Diversification Improvement Simulations. Renders interactive
// comparison cards for all six strategies (Current, Equal Weight 1/N, Inverse
// Volatility, Hierarchical Risk Parity, Equal Risk Contribution / True Risk
// Parity, and Maximum Diversification Ratio), a Recharts grouped Current vs.
// Target Allocation & Risk-Contribution chart, and an actionable Rebalancing
// Trade Blotter surfacing exact weight deltas (Δwᵢ) and one-way turnover.
//
// Units note: weights, PCTR, portfolio_volatility, cvar_95_daily and
// turnover_one_way are FRACTIONS (0..1) and are multiplied by 100 for display.
// volatility_delta_pct and enb_uplift_pct are also FRACTIONS (e.g. -0.15 =
// -15%) per the backend (`(vol - current_vol) / current_vol`).
//
// Themed for the shared light/dark dashboard shell.

import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowDownRight,
  ArrowUpRight,
  Award,
  Minus,
  Scale,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingDown,
  Wallet,
} from "lucide-react";

import type {
  DiversificationSimulations,
  StrategyAllocationRow,
  StrategyId,
} from "../types";
import { useSymbolRoutes } from "../useSymbolRoutes";
import { SymbolLink } from "./SymbolLink";

interface OptimizationSimulatorPanelProps {
  simulations: DiversificationSimulations;
}

const CHART_GRID = "#e2e8f0";
const CHART_AXIS = "#94a3b8";
const TOOLTIP_STYLE: React.CSSProperties = {
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  fontSize: 12,
  background: "rgba(255,255,255,0.97)",
  color: "#0f172a",
};

const cardClass =
  "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/90";

// Trades below this absolute weight delta (0.5%) are treated as holds -- too
// small to be worth an execution ticket.
const TRADE_EPSILON = 0.005;

type ChartMode = "allocation" | "risk";

const STRATEGY_ACCENT: Record<StrategyId, string> = {
  current: "#64748b",
  equal_weight: "#0ea5e9",
  inverse_volatility: "#14b8a6",
  hrp: "#6366f1",
  erc_risk_parity: "#8b5cf6",
  max_diversification: "#f59e0b",
};

function formatPct(fraction: number, digits = 1): string {
  return `${(fraction * 100).toFixed(digits)}%`;
}

function formatSignedPct(fraction: number, digits = 1): string {
  const v = fraction * 100;
  return `${v >= 0 ? "+" : ""}${v.toFixed(digits)}%`;
}

export const OptimizationSimulatorPanel: React.FC<OptimizationSimulatorPanelProps> = ({
  simulations,
}) => {
  const { strategies, recommended_strategy_id, min_weight_constraint, max_weight_constraint } =
    simulations;

  const currentStrategy = useMemo(
    () => strategies.find((s) => s.strategy_id === "current") ?? strategies[0],
    [strategies]
  );

  // Default selection: the recommended strategy if present, otherwise the first
  // non-current optimizer so the chart/blotter show a meaningful rebalance.
  const defaultSelected = useMemo<StrategyId>(() => {
    const rec = strategies.find((s) => s.strategy_id === recommended_strategy_id);
    if (rec) return rec.strategy_id;
    const firstOptimizer = strategies.find((s) => s.strategy_id !== "current");
    return (firstOptimizer ?? strategies[0]).strategy_id;
  }, [strategies, recommended_strategy_id]);

  const [selectedId, setSelectedId] = useState<StrategyId>(defaultSelected);
  const [chartMode, setChartMode] = useState<ChartMode>("allocation");

  const selectedStrategy = useMemo(
    () => strategies.find((s) => s.strategy_id === selectedId) ?? currentStrategy,
    [strategies, selectedId, currentStrategy]
  );

  // Grouped chart data -- allocation (capital weights) or risk contribution.
  const chartData = useMemo(() => {
    const rows = [...selectedStrategy.allocations];
    rows.sort((a, b) =>
      chartMode === "allocation"
        ? b.target_weight - a.target_weight
        : b.target_pctr - a.target_pctr
    );
    return rows.map((r) => ({
      ticker: r.ticker,
      current: Number(
        ((chartMode === "allocation" ? r.current_weight : r.current_pctr) * 100).toFixed(2)
      ),
      target: Number(
        ((chartMode === "allocation" ? r.target_weight : r.target_pctr) * 100).toFixed(2)
      ),
    }));
  }, [selectedStrategy, chartMode]);

  // Trade blotter -- sorted by the magnitude of the rebalance.
  const blotter = useMemo(() => {
    const rows = [...selectedStrategy.allocations];
    rows.sort((a, b) => Math.abs(b.weight_delta) - Math.abs(a.weight_delta));
    return rows;
  }, [selectedStrategy.allocations]);

  const blotterSummary = useMemo(() => {
    let buys = 0;
    let sells = 0;
    let buyNotional = 0;
    let sellNotional = 0;
    for (const row of selectedStrategy.allocations) {
      if (row.weight_delta > TRADE_EPSILON) {
        buys += 1;
        buyNotional += row.weight_delta;
      } else if (row.weight_delta < -TRADE_EPSILON) {
        sells += 1;
        sellNotional += Math.abs(row.weight_delta);
      }
    }
    return { buys, sells, buyNotional, sellNotional };
  }, [selectedStrategy.allocations]);

  // Resolve every ticker across all strategies to its deep-dive href once, so
  // the trade-insights table can link each symbol to its asset info page.
  const allTickers = useMemo(
    () =>
      Array.from(
        new Set(strategies.flatMap((s) => s.allocations.map((a) => a.ticker))),
      ),
    [strategies]
  );
  const routes = useSymbolRoutes(allTickers);

  const isCurrentSelected = selectedStrategy.strategy_id === "current";

  return (
    <div className="space-y-6">
      {/* Strategy Comparison Cards */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Scale className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
            <h3 className="text-sm font-semibold text-slate-800 dark:text-white">
              Rebalancing Strategy Simulations
            </h3>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-0.5 font-mono text-[11px] text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
            Box constraints [{formatPct(min_weight_constraint, 0)}, {formatPct(max_weight_constraint, 0)}]
          </span>
        </div>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Six institutional allocation engines simulated under identical box constraints. Select a
          card to drive the allocation chart and the actionable trade blotter below.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {strategies.map((strategy) => {
            const isSelected = strategy.strategy_id === selectedId;
            const isRecommended = strategy.strategy_id === recommended_strategy_id;
            const isCurrent = strategy.strategy_id === "current";
            const accent = STRATEGY_ACCENT[strategy.strategy_id] ?? "#6366f1";
            return (
              <button
                key={strategy.strategy_id}
                type="button"
                onClick={() => setSelectedId(strategy.strategy_id)}
                className={`group relative flex flex-col rounded-2xl border p-4 text-left transition ${
                  isSelected
                    ? "border-indigo-500 bg-indigo-50/60 ring-2 ring-indigo-500/40 dark:border-indigo-400 dark:bg-indigo-500/10"
                    : "border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900/90 dark:hover:border-indigo-500/50"
                }`}
              >
                {isRecommended && (
                  <span className="absolute -top-2.5 right-3 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow">
                    <Award className="h-3 w-3" /> Recommended
                  </span>
                )}

                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: accent }}
                    />
                    <h4 className="text-sm font-semibold text-slate-800 dark:text-white">
                      {strategy.strategy_name}
                    </h4>
                  </div>
                  <span className="shrink-0 font-mono text-lg font-bold text-slate-900 dark:text-white">
                    {strategy.diversification_score.toFixed(0)}
                  </span>
                </div>

                <p className="mt-1.5 line-clamp-2 min-h-[2rem] text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                  {strategy.description}
                </p>

                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-slate-200/70 pt-3 text-[11px] dark:border-slate-800/70">
                  <div>
                    <div className="text-slate-400 dark:text-slate-500">Volatility</div>
                    <div className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                      {formatPct(strategy.portfolio_volatility)}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-400 dark:text-slate-500">Effective Bets</div>
                    <div className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                      {strategy.effective_number_of_bets.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-400 dark:text-slate-500">Div. Ratio</div>
                    <div className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                      {strategy.diversification_ratio.toFixed(2)}x
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-400 dark:text-slate-500">Turnover</div>
                    <div className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                      {isCurrent ? "—" : formatPct(strategy.turnover_one_way)}
                    </div>
                  </div>
                </div>

                {!isCurrent && (
                  <div className="mt-3 flex items-center justify-between border-t border-slate-200/70 pt-2.5 text-[11px] dark:border-slate-800/70">
                    <span
                      className={`inline-flex items-center gap-1 font-mono font-semibold ${
                        strategy.volatility_delta_pct <= 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-rose-600 dark:text-rose-400"
                      }`}
                    >
                      <TrendingDown className="h-3.5 w-3.5" />
                      Vol {formatSignedPct(strategy.volatility_delta_pct)}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 font-mono font-semibold ${
                        strategy.enb_uplift_pct >= 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-rose-600 dark:text-rose-400"
                      }`}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      ENB {formatSignedPct(strategy.enb_uplift_pct)}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grouped Allocation / Risk Chart + Blotter Summary */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        {/* Chart */}
        <div className={`${cardClass} xl:col-span-8`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
                <Target className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
                Current vs. Target {chartMode === "allocation" ? "Allocation" : "Risk Contribution"}
                <span className="font-normal text-slate-400 dark:text-slate-500">
                  · {selectedStrategy.strategy_name}
                </span>
              </h3>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {chartMode === "allocation"
                  ? "Capital weight per holding, current book vs. simulated target."
                  : "Percentage contribution to total risk (Euler PCTR), current vs. target."}
              </p>
            </div>

            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5 text-xs dark:border-slate-700 dark:bg-slate-800">
              <button
                type="button"
                onClick={() => setChartMode("allocation")}
                className={`rounded-md px-3 py-1.5 font-medium transition ${
                  chartMode === "allocation"
                    ? "bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-400"
                    : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              >
                Allocation
              </button>
              <button
                type="button"
                onClick={() => setChartMode("risk")}
                className={`rounded-md px-3 py-1.5 font-medium transition ${
                  chartMode === "risk"
                    ? "bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-400"
                    : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              >
                Risk Contribution
              </button>
            </div>
          </div>

          <div className="mt-4 h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
                <XAxis
                  dataKey="ticker"
                  tick={{ fontSize: 11, fill: CHART_AXIS }}
                  axisLine={{ stroke: CHART_GRID }}
                  tickLine={false}
                  interval={0}
                  angle={chartData.length > 8 ? -35 : 0}
                  textAnchor={chartData.length > 8 ? "end" : "middle"}
                  height={chartData.length > 8 ? 54 : 24}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: CHART_AXIS }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v}%`}
                  width={44}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  cursor={{ fill: "rgba(99,102,241,0.08)" }}
                  formatter={(value: number, name: string) => [
                    `${value.toFixed(2)}%`,
                    name === "current" ? "Current" : "Target",
                  ]}
                />
                <Legend
                  verticalAlign="top"
                  height={28}
                  iconType="circle"
                  formatter={(value) => (
                    <span className="text-xs text-slate-600 dark:text-slate-300">
                      {value === "current" ? "Current" : "Target"}
                    </span>
                  )}
                />
                <Bar dataKey="current" name="current" fill="#94a3b8" radius={[3, 3, 0, 0]} maxBarSize={28} />
                <Bar dataKey="target" name="target" radius={[3, 3, 0, 0]} maxBarSize={28}>
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.ticker}
                      fill={STRATEGY_ACCENT[selectedStrategy.strategy_id] ?? "#6366f1"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Blotter summary / selected strategy headline metrics */}
        <div className={`${cardClass} flex flex-col xl:col-span-4`}>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
            <Wallet className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
            Rebalance Summary
          </h3>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            One-way turnover required to migrate from the current book to{" "}
            <strong className="text-slate-700 dark:text-slate-200">
              {selectedStrategy.strategy_name}
            </strong>
            .
          </p>

          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-center dark:border-slate-800 dark:bg-slate-950/50">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
              One-Way Turnover
            </div>
            <div className="mt-1 font-mono text-3xl font-bold text-indigo-600 dark:text-indigo-400">
              {isCurrentSelected ? "0.0%" : formatPct(selectedStrategy.turnover_one_way)}
            </div>
            <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              0.5 × Σ|Δwᵢ| of portfolio notional traded
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                <ArrowUpRight className="h-3.5 w-3.5" /> Buys
              </div>
              <div className="mt-1 font-mono text-lg font-bold text-emerald-700 dark:text-emerald-300">
                {blotterSummary.buys}
              </div>
              <div className="text-[10px] text-emerald-600/80 dark:text-emerald-400/80">
                +{formatPct(blotterSummary.buyNotional)} notional
              </div>
            </div>
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-rose-700 dark:text-rose-300">
                <ArrowDownRight className="h-3.5 w-3.5" /> Sells
              </div>
              <div className="mt-1 font-mono text-lg font-bold text-rose-700 dark:text-rose-300">
                {blotterSummary.sells}
              </div>
              <div className="text-[10px] text-rose-600/80 dark:text-rose-400/80">
                -{formatPct(blotterSummary.sellNotional)} notional
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-2 border-t border-slate-200/70 pt-4 text-xs dark:border-slate-800/70">
            <div className="flex items-center justify-between">
              <span className="text-slate-500 dark:text-slate-400">Diversification Score</span>
              <span className="font-mono font-semibold text-slate-800 dark:text-white">
                {currentStrategy.diversification_score.toFixed(1)} →{" "}
                {selectedStrategy.diversification_score.toFixed(1)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500 dark:text-slate-400">95% Daily CVaR</span>
              <span className="font-mono font-semibold text-slate-800 dark:text-white">
                -{formatPct(currentStrategy.cvar_95_daily, 2)} → -
                {formatPct(selectedStrategy.cvar_95_daily, 2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500 dark:text-slate-400">Risk HHI</span>
              <span className="font-mono font-semibold text-slate-800 dark:text-white">
                {currentStrategy.hhi_risk.toFixed(3)} → {selectedStrategy.hhi_risk.toFixed(3)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Actionable Rebalancing Trade Blotter */}
      <div className={cardClass}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
            <ShieldCheck className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
            Actionable Rebalancing Trade Insights
          </h3>
          <span className="text-[11px] text-slate-400 dark:text-slate-500">
            Ordered by |Δwᵢ| · holds under {formatPct(TRADE_EPSILON, 1)} suppressed
          </span>
        </div>

        {isCurrentSelected ? (
          <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-400">
            This is your current book — no trades required. Select an optimizer strategy above to
            generate executable deltas.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-400">
                <tr>
                  <th className="py-2.5 pl-4 pr-2">Ticker</th>
                  <th className="px-2 py-2.5 text-right">Current</th>
                  <th className="px-2 py-2.5 text-right">Target</th>
                  <th className="px-2 py-2.5 text-right">Δ Weight</th>
                  <th className="px-2 py-2.5">Rebalance</th>
                  <th className="py-2.5 pl-2 pr-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/70 dark:divide-slate-800/60">
                {blotter.map((row) => (
                  <BlotterRow key={row.ticker} row={row} routes={routes} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Single trade blotter row -- a buy / sell / hold ticket with a signed delta bar.
const BlotterRow: React.FC<{
  row: StrategyAllocationRow;
  routes?: Record<string, string>;
}> = ({ row, routes }) => {
  const deltaPct = row.weight_delta * 100;
  const isBuy = row.weight_delta > TRADE_EPSILON;
  const isSell = row.weight_delta < -TRADE_EPSILON;

  const action = isBuy
    ? { label: "BUY", classes: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", icon: ArrowUpRight }
    : isSell
    ? { label: "SELL", classes: "bg-rose-500/15 text-rose-700 dark:text-rose-300", icon: ArrowDownRight }
    : { label: "HOLD", classes: "bg-slate-200/70 text-slate-500 dark:bg-slate-800 dark:text-slate-400", icon: Minus };
  const ActionIcon = action.icon;

  // Delta bar scaled to a ±25pp reference window for readability.
  const barWidth = Math.min(100, (Math.abs(deltaPct) / 25) * 100);

  return (
    <tr className="hover:bg-slate-50/70 dark:hover:bg-slate-900/40">
      <td className="py-2.5 pl-4 pr-2">
        <SymbolLink symbol={row.ticker} routes={routes} />
      </td>
      <td className="px-2 py-2.5 text-right font-mono text-slate-600 dark:text-slate-300">
        {(row.current_weight * 100).toFixed(1)}%
      </td>
      <td className="px-2 py-2.5 text-right font-mono font-semibold text-slate-800 dark:text-white">
        {(row.target_weight * 100).toFixed(1)}%
      </td>
      <td
        className={`px-2 py-2.5 text-right font-mono font-semibold ${
          isBuy
            ? "text-emerald-600 dark:text-emerald-400"
            : isSell
            ? "text-rose-600 dark:text-rose-400"
            : "text-slate-400 dark:text-slate-500"
        }`}
      >
        {deltaPct >= 0 ? "+" : ""}
        {deltaPct.toFixed(1)}pp
      </td>
      <td className="px-2 py-2.5">
        <div className="flex h-2 w-full min-w-[80px] items-center">
          <div className="flex w-1/2 justify-end">
            {isSell && (
              <div
                className="h-2 rounded-l-full bg-rose-500/70"
                style={{ width: `${barWidth}%` }}
              />
            )}
          </div>
          <div className="h-3 w-px shrink-0 bg-slate-300 dark:bg-slate-700" />
          <div className="flex w-1/2 justify-start">
            {isBuy && (
              <div
                className="h-2 rounded-r-full bg-emerald-500/70"
                style={{ width: `${barWidth}%` }}
              />
            )}
          </div>
        </div>
      </td>
      <td className="py-2.5 pl-2 pr-4 text-center">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${action.classes}`}
        >
          <ActionIcon className="h-3 w-3" />
          {action.label}
        </span>
      </td>
    </tr>
  );
};
